/**
 * Reads a dropped file into plain text in the browser.
 *
 * In production the file goes straight to S3 and Claude reads it server side,
 * including scanned PDFs (STE-536 Part 3). This local reader is what the app
 * uses to show the person what was picked up before the upload starts, and it
 * is what the mock API extracts from while the backend is being built.
 */

async function inflateRaw(bytes: Uint8Array): Promise<string> {
  const stream = new Blob([bytes as unknown as BlobPart])
    .stream()
    .pipeThrough(new DecompressionStream('deflate-raw'))
  return new Response(stream).text()
}

function docxXmlToText(xml: string): string {
  return xml
    .replace(/<w:tab[^>]*\/?>/g, ' ')
    .replace(/<w:br[^>]*\/?>/g, '\n')
    .replace(/<\/w:p>/g, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;|&#39;/g, "'")
    .replace(/&amp;/g, '&')
    .split('\n')
    .map((l) => l.replace(/[ \t ]+/g, ' ').trim())
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

/** The words put in front of a line of each career track (FR-66). */
const TRACK_MARK = {
  pl: 'People leader track: ',
  ic: 'Individual contributor track: ',
} as const

type Track = keyof typeof TRACK_MARK

/**
 * Which track a font colour stands for, by its hue: red for the people leader
 * track, green for the individual contributor track, anything else neither.
 * The same rule as the server's reader in `backend/src/lib/docx.ts`, so what
 * the person is shown before upload is what the server reads.
 */
function trackOfColour(hex: string | undefined): Track | undefined {
  if (!hex || !/^[0-9a-f]{6}$/i.test(hex)) return undefined
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255) as [number, number, number]
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  if (max < 0.3 || max - min < 0.4 * max) return undefined
  const d = max - min
  const hue = max === r ? (60 * ((g - b) / d) + 360) % 360 : max === g ? 60 * ((b - r) / d) + 120 : 60 * ((r - g) / d) + 240
  if (hue <= 15 || hue >= 345) return 'pl'
  if (hue >= 75 && hue <= 165) return 'ic'
  return undefined
}

interface Line { at: number; pl: number; ic: number; other: number }

/**
 * A Word document's text with each line of a career track marked in words
 * (FR-66), or the plain text untouched when it is not a document of tracks
 * (FR-66a). See the server's copy for why each rule is there.
 */
function docxTrackText(xml: string): string {
  const plain = docxXmlToText(xml)
  if (!/track/i.test(plain)) return plain
  const lines: Line[] = []
  const open: Line[] = []
  let inChange = 0
  let inText = false
  let colour: string | undefined
  const tag = /<(\/?)([\w:]+)([^>]*?)(\/?)>/g
  let last = 0
  for (let m = tag.exec(xml); m; m = tag.exec(xml)) {
    if (inText && open.length) {
      const words = xml.slice(last, m.index).replace(/&[^;\s]*;/g, ' ')
      const count = (words.match(/[\p{L}\p{N}]/gu) ?? []).length
      if (count) open[open.length - 1]![trackOfColour(colour) ?? 'other'] += count
    }
    last = m.index + m[0].length
    const [, closing, name, attrs, selfClosing] = m
    if (name === 'w:p') {
      if (closing) {
        const line = open.pop()
        if (line) lines.push(line)
      } else if (!selfClosing) {
        open.push({ at: last, pl: 0, ic: 0, other: 0 })
      }
    } else if (name === 'w:br' && open.length) {
      lines.push(open.pop()!)
      open.push({ at: last, pl: 0, ic: 0, other: 0 })
    } else if (name === 'w:rPrChange' && !selfClosing) {
      inChange += closing ? -1 : 1
    } else if (name === 'w:r' && !closing) {
      colour = undefined
    } else if (name === 'w:color' && !closing && !inChange) {
      colour = /w:val="([^"]*)"/.exec(attrs!)?.[1]
    } else if (name === 'w:t' || name === 'w:delText') {
      inText = !closing && !selfClosing
    }
  }
  const trackOf = (l: Line): Track | undefined =>
    l.other ? undefined : l.pl && !l.ic ? 'pl' : l.ic && !l.pl ? 'ic' : undefined
  const marked = lines.map((l) => ({ at: l.at, track: trackOf(l) })).filter((l) => l.track)
  if (!marked.some((l) => l.track === 'pl') || !marked.some((l) => l.track === 'ic')) return plain
  marked.sort((a, b) => a.at - b.at)
  let out = ''
  let from = 0
  for (const l of marked) {
    out += xml.slice(from, l.at) + TRACK_MARK[l.track!]
    from = l.at
  }
  return docxXmlToText(out + xml.slice(from))
}

/**
 * Pulls named entries out of a zip, as text.
 *
 * A `.docx` and an `.xlsx` are both zips of XML, so both readers want the same
 * walk of the central directory. It was written for Word and is now shared,
 * which is the same shape the server side takes.
 */
export async function zipEntries(
  buf: ArrayBuffer,
  wanted: (name: string) => boolean,
): Promise<Map<string, string>> {
  const u8 = new Uint8Array(buf)
  const dv = new DataView(buf)
  const dec = new TextDecoder()
  let eocd = -1
  const floor = Math.max(0, u8.length - 65558)
  for (let i = u8.length - 22; i >= floor; i--) {
    if (dv.getUint32(i, true) === 0x06054b50) {
      eocd = i
      break
    }
  }
  if (eocd < 0) throw new Error('That file is not a zip, so it is not a Word or Excel file.')
  const count = dv.getUint16(eocd + 10, true)
  let p = dv.getUint32(eocd + 16, true)
  const out = new Map<string, string>()
  for (let n = 0; n < count; n++) {
    if (p + 46 > u8.length || dv.getUint32(p, true) !== 0x02014b50) break
    const method = dv.getUint16(p + 10, true)
    const comp = dv.getUint32(p + 20, true)
    const nameLen = dv.getUint16(p + 28, true)
    const extraLen = dv.getUint16(p + 30, true)
    const cmtLen = dv.getUint16(p + 32, true)
    const lho = dv.getUint32(p + 42, true)
    const name = dec.decode(u8.subarray(p + 46, p + 46 + nameLen))
    if (wanted(name)) {
      const start = lho + 30 + dv.getUint16(lho + 26, true) + dv.getUint16(lho + 28, true)
      const raw = u8.subarray(start, start + comp)
      out.set(name, method === 0 ? dec.decode(raw) : await inflateRaw(raw))
    }
    p += 46 + nameLen + extraLen + cmtLen
  }
  return out
}

async function docxText(buf: ArrayBuffer): Promise<string> {
  const files = await zipEntries(buf, (n) => n === 'word/document.xml')
  const xml = files.get('word/document.xml')
  if (!xml) throw new Error('That .docx has no readable document inside it.')
  return docxTrackText(xml)
}

export async function readFileText(file: File): Promise<string> {
  const name = file.name || 'file'
  if (/\.docx$/i.test(name)) return docxText(await file.arrayBuffer())
  if (/\.doc$/i.test(name)) {
    throw new Error(`${name} is a legacy .doc. Save it as .docx or PDF and try again.`)
  }
  if (/\.pdf$/i.test(name)) {
    // The server reads PDFs, including scanned ones. Nothing to show locally.
    return ''
  }
  return file.text()
}

export const isPdf = (file: File) => /\.pdf$/i.test(file.name || '')

/** The columns a mass upload spreadsheet starts with. */
const BULK_HEADER = /^\s*"?title"?\s*,\s*"?family"?\s*,/i

/**
 * Whether this looks like a spreadsheet of roles rather than one written role.
 *
 * Checked by name and by content, because the file picker's `accept` list is
 * only a suggestion: dragging a file onto the drop zone bypasses it entirely,
 * which is how a spreadsheet of every role in the library was dropped onto
 * "New job description" and read as one role.
 */
export const looksLikeSpreadsheet = (name: string, text = '') =>
  /\.(csv|tsv|xlsx|xls)$/i.test(name) || BULK_HEADER.test(text)

/**
 * Whether this is a file kind the reader takes at all.
 *
 * Stated as what is allowed rather than what is not. A list of banned
 * extensions is a list somebody has to keep adding to, and the one thing it is
 * guaranteed to miss is whatever a customer drops next: `Main.java` got through
 * that way and became a role titled `<UNKNOWN>`.
 */
export const isDocumentFile = (name: string) => /\.(pdf|docx|txt|md|text)$/i.test(name)
